import React, {useState,useEffect,useContext} from 'react'

import {Alert, Button, Checkbox, DatePicker, Form, Input, Layout, Modal, Select, Space, Spin, Table} from "antd";

import {errorN, successN} from "../../general";
import {navigate} from "hookrouter";
import useFetch from "use-http";
import {DollarOutlined, UndoOutlined, WarningOutlined} from '@ant-design/icons'
import {isMobile} from "react-device-detect";

import {Context} from "../../store"
import moment from 'moment'
import {getChannelAccess, getSyncPause, isChannelAdmin} from "./channelAccess";

const {Content} = Layout;
const {Option} = Select;

// A price change bigger than this (as a fraction of the old price) needs an extra tick before saving.
const BIG_CHANGE = 0.5;

// Online Rate Control (daily prices), matches Windows frmAdmin_DailyRate.cs.
// With mode="dailyrate" it is the Settings > Daily Rate page: Windows opens the same form from
// Administrator/Supervisor > Rate > Daily Rate for administrator, manager and supervisor,
// whether or not a channel is set up.
function RateControl(props) {
    const [state] = useContext(Context)
    const { get, post, response, loading} = useFetch()

    const [property, setProperty] = useState(null)
    const [propertyList, setPropertyList] = useState([])
    const [startDate, setStartDate] = useState(moment().startOf('day'))
    const [endDate, setEndDate] = useState(moment().startOf('day').add(1, 'days'))

    const [rows, setRows] = useState([])
    const [dates, setDates] = useState([])
    const [loaded, setLoaded] = useState(null)
    const [pending, setPending] = useState({})
    const [drafts, setDrafts] = useState({})
    const [saving, setSaving] = useState(false)

    const access = getChannelAccess(state.role);
    const admin = isChannelAdmin(state.role !== undefined && state.role !== null && state.role !== '' ? state.role : localStorage.getItem('role'));
    const syncPause = getSyncPause();
    const dailyRateMode = props.mode === 'dailyrate';
    const title = dailyRateMode ? 'Daily Rate' : 'Online Rate Control';
    const allowed = access.rateControl === true || (dailyRateMode && admin);
    const pendingCount = Object.keys(pending).length;

    useEffect(() => {

        if(localStorage.getItem('session') === null)
        {
            navigate('/')
        }
        else
        {
            let current = JSON.parse(localStorage.getItem('currentproperty'));
            let list = [];

            try
            {
                list = JSON.parse(localStorage.getItem('properties'));
            }
            catch (err)
            {
                list = [];
            }

            if(list === undefined || list === null || !Array.isArray(list))
            {
                list = [];
            }

            if(current !== null && !list.some(x => x.id.toString() === current.id.toString()))
            {
                list.push(current);
            }

            setPropertyList(list);
            setProperty(current !== null ? current.id : null);
        }

        return () => {

        }
    },[state.currentproperty])

    function clearGrid() {
        setPending({});
        setDrafts({});
        setRows([]);
        setDates([]);
        setLoaded(null);
    }

    // Changing property or dates throws away pending changes, so ask first.
    function confirmDiscard(apply) {
        if(pendingCount === 0)
        {
            apply();
            return;
        }

        Modal.confirm({
            title: 'Discard pending changes?',
            content: `You have ${pendingCount} unsaved change(s). They will be lost.`,
            okText: 'Discard',
            cancelText: 'Keep',
            onOk: () => {
                clearGrid();
                apply();
            }
        });
    }

    function onPropertyChange(e) {
        confirmDiscard(() => { setProperty(e); clearGrid(); });
    }

    function onStartChange(e) {
        if(e === null) return;
        confirmDiscard(() => { setStartDate(e.clone().startOf('day')); clearGrid(); });
    }

    function onEndChange(e) {
        if(e === null) return;
        confirmDiscard(() => { setEndDate(e.clone().startOf('day')); clearGrid(); });
    }

    async function loadData(hotel, s, e) {

        if(hotel === null || hotel === undefined)
        {
            errorN(`Please choose a property.`);
            return;
        }

        if(e.isBefore(s, 'day'))
        {
            errorN(`End date must be on or after the start date.`);
            return;
        }

        let start = s.format('YYYY-MM-DD');
        let end = e.clone().add(1, 'days').format('YYYY-MM-DD');

        let ls = [];
        for (let d = s.clone(); !d.isAfter(e, 'day'); d.add(1, 'days'))
        {
            ls.push(d.format('YYYY-MM-DD'));
        }

        try
        {
            let availabilityList = await get(`/api/hotels/${hotel}/availability_rates?start=${start}&end=${end}`)
            if (response.ok)
            {
                // only "daily" rates, grouped by rate plan in the order the plans first appear (as Windows does)
                let plans = [];
                let byPlan = {};

                for(let i of (availabilityList || []))
                {
                    for(let j of (i.rates || []))
                    {
                        if(j.type !== 'daily' || j.data === null || j.data === undefined)
                        {
                            continue;
                        }

                        let plan = j.data.plan !== undefined && j.data.plan !== null ? j.data.plan : '';
                        if(byPlan[plan] === undefined)
                        {
                            byPlan[plan] = [];
                            plans.push(plan);
                        }

                        let prices = {};
                        for(let y of (j.daily || []))
                        {
                            prices[moment(y.date).format('YYYY-MM-DD')] = y.rate;
                        }

                        byPlan[plan].push({
                            key: `r${j.id}`,
                            id: j.id,
                            plan: plan,
                            name: j.data.name,
                            prices: prices
                        });
                    }
                }

                let list = [];
                for(let plan of plans)
                {
                    list.push({key: `p${plan}`, group: true, plan: plan, count: byPlan[plan].length});
                    for(let r of byPlan[plan])
                    {
                        list.push(r);
                    }
                }

                setDates(ls);
                setRows(list);
                setPending({});
                setDrafts({});
                setLoaded({hotel: hotel, start: s.clone(), end: e.clone()});
            }
            else
            {
                if(response.status === 401)
                {
                    localStorage.removeItem('session');
                    window.location.reload();
                }
                else
                {
                    errorN(`Load Data - ${response.status}: ${response.statusText}. Please contact the administrator.`);
                }
            }
        }
        catch(err)
        {
            errorN(`Error Load Data: ${err}. Please contact the administrator.`);
        }
    }

    function onLoad() {
        confirmDiscard(() => { loadData(property, startDate, endDate); });
    }

    function isBigChange(from, to) {
        let old = Number(from);
        return isNaN(old) || old <= 0 || Math.abs(to - old) / old > BIG_CHANGE;
    }

    function commitCell(row, date) {
        let key = `${row.id}|${date}`;
        if(drafts[key] === undefined)
        {
            return;
        }

        let text = drafts[key].trim();
        let nextDrafts = {...drafts};
        delete nextDrafts[key];
        setDrafts(nextDrafts);

        if(!/^\d+(\.\d{1,2})?$/.test(text))
        {
            errorN(`Value is not numeric. Please enter a price with up to 2 decimals.`);
            return;
        }

        let value = Math.round(parseFloat(text) * 100) / 100;

        if(value <= 0)
        {
            errorN(`Price must be more than 0.`);
            return;
        }

        let old = row.prices[date];
        let next = {...pending};

        if(old !== undefined && old !== null && Number(old) === value)
        {
            delete next[key];
        }
        else
        {
            next[key] = {
                id: row.id,
                plan: row.plan,
                name: row.name,
                date: date,
                from: old,
                to: value,
                big: isBigChange(old, value)
            };
        }

        setPending(next);
    }

    function undoCell(key) {
        let next = {...pending};
        delete next[key];
        setPending(next);

        let nextDrafts = {...drafts};
        delete nextDrafts[key];
        setDrafts(nextDrafts);
    }

    function showPrice(v) {
        return v === undefined || v === null ? '-' : Number(v).toFixed(2);
    }

    function onSave() {
        if(pendingCount === 0 || loaded === null)
        {
            return;
        }

        if(Object.keys(drafts).length > 0)
        {
            errorN(`Please finish editing the cell (press Enter) before saving.`);
            return;
        }

        let changes = Object.values(pending).sort((a, b) => a.plan.localeCompare(b.plan) || a.name.localeCompare(b.name) || a.date.localeCompare(b.date));
        let bigCount = changes.filter(c => c.big).length;
        let ticked = {value: false};

        let modal = Modal.confirm({
            title: `Push ${changes.length} price change(s) to the channel?`,
            width: isMobile === true ? '100%' : 600,
            content: (
                <div>
                    <div style={{maxHeight: '45vh', overflow: 'auto'}}>
                        {changes.map(c =>
                            <div key={`${c.id}|${c.date}`} style={{color: c.big ? '#cf1322' : undefined}}>
                                {c.big ? <WarningOutlined /> : null}
                                {` ${c.plan} / ${c.name}, ${moment(c.date).format('DD/MM/YYYY')}: ${showPrice(c.from)} -> ${showPrice(c.to)}`}
                            </div>
                        )}
                    </div>
                    {bigCount > 0 ?
                        <div style={{marginTop: '10px'}}>
                            <Alert type="warning" showIcon message={`${bigCount} change(s) are more than 50% from the old price (shown in red).`} />
                            <Checkbox style={{marginTop: '8px'}} onChange={(ev) => {
                                ticked.value = ev.target.checked;
                                modal.update({okButtonProps: {disabled: !ev.target.checked}});
                            }}>I have checked these prices and want to save them.</Checkbox>
                        </div>
                        : null
                    }
                </div>
            ),
            okText: 'Save',
            cancelText: 'Cancel',
            okButtonProps: {disabled: bigCount > 0},
            onOk: () => {
                if(bigCount > 0 && ticked.value !== true)
                {
                    return Promise.reject();
                }
                return saveData(changes);
            }
        });
    }

    async function saveData(changes) {
        if(saving === true)
        {
            return;
        }

        setSaving(true);

        let body = {
            rates: changes.map(c => ({rate: parseInt(c.id), price: c.to, date: c.date})),
            hotel: parseInt(loaded.hotel),
            start: loaded.start.format('YYYY-MM-DD'),
            end: loaded.end.format('YYYY-MM-DD')
        };

        try
        {
            await post(`/api/calendar_rate`, body)
            if (response.ok)
            {
                successN(`${title}: ${changes.length} price change(s) saved.`);
            }
            else
            {
                if(response.status === 401)
                {
                    localStorage.removeItem('session');
                    window.location.reload();
                    return;
                }
                else
                {
                    errorN(`Save - ${response.status}: ${response.statusText}. Please contact the administrator.`);
                }
            }
        }
        catch(err)
        {
            errorN(`Error Save: ${err}. Please contact the administrator.`);
        }

        setSaving(false);
        await loadData(loaded.hotel, loaded.start, loaded.end);
    }

    // A rate plan header row spans the three fixed columns; its date cells stay empty.
    let columns = [
        {title: 'ID', dataIndex: 'id', key: 'id', fixed: 'left', width: 60,
            render: (text, row) => row.group ? {children: <b>{`Rate Plan: ${row.plan} (${row.count})`}</b>, props: {colSpan: 3, style: {background: '#fafafa'}}} : text},
        {title: 'Rate Plan', dataIndex: 'plan', key: 'plan', fixed: 'left', width: 110,
            render: (text, row) => row.group ? {children: null, props: {colSpan: 0}} : text},
        {title: 'Rate Name', dataIndex: 'name', key: 'name', fixed: 'left', width: 160,
            render: (text, row) => row.group ? {children: null, props: {colSpan: 0}} : text},
    ];

    for(let date of dates)
    {
        columns.push({
            title: moment(date).format('DD/MM ddd'),
            key: date,
            width: 105,
            align: 'center',
            onCell: (row) => ({
                style: {
                    background: row.group ? '#fafafa' : pending[`${row.id}|${date}`] === undefined ? undefined : (pending[`${row.id}|${date}`].big ? '#ffccc7' : '#ffe58f')
                }
            }),
            render: (text, row) => {
                if(row.group)
                {
                    return null;
                }

                let key = `${row.id}|${date}`;
                let change = pending[key];
                let old = row.prices[date];

                return (
                    <div>
                        <Input
                            size="small"
                            style={{width: '75px', textAlign: 'right'}}
                            disabled={saving}
                            value={drafts[key] !== undefined ? drafts[key] : (change !== undefined ? showPrice(change.to) : (old === undefined || old === null ? '' : showPrice(old)))}
                            onChange={(ev) => setDrafts({...drafts, [key]: ev.target.value})}
                            onBlur={() => commitCell(row, date)}
                            onPressEnter={() => commitCell(row, date)}
                        />
                        {change !== undefined ?
                            <div style={{fontSize: '9px'}}>
                                {`${showPrice(change.from)} -> ${showPrice(change.to)} `}
                                <UndoOutlined title="Undo" style={{cursor: 'pointer'}} onClick={() => undoCell(key)} />
                            </div>
                            : null
                        }
                    </div>
                );
            }
        });
    }

    if(allowed !== true)
    {
        return (
            <>
                <div style={{ backgroundImage:`url("/header-bg7.png")`, color:'black', fontSize: '18px', paddingLeft: '15px', paddingTop: '15px',paddingBottom: '15px',marginTop: '0px'}}>
                    &nbsp;&nbsp;<DollarOutlined />
                    &nbsp;&nbsp;{title}
                </div>
                <div style={{padding: '20px'}}>
                    <Alert type="error" showIcon message={`You do not have access to ${title}.`} />
                </div>
            </>
        );
    }

    return (
        <>
        <div style={{ backgroundImage:`url("/header-bg7.png")`, color:'black', fontSize: '18px', paddingLeft: '15px', paddingTop: '15px',paddingBottom: '15px',marginTop: '0px'}}>
        &nbsp;&nbsp;<DollarOutlined />
        &nbsp;&nbsp;{title}
        </div>
        <Layout style={{height: isMobile === true ? '70vh':'74vh'}}>

            <Spin spinning={loading || saving}>
            <Content>
                <div style={{padding: '20px', margin: '0px 0',height:'72vh',overflow:'auto'}}>

                    {syncPause !== null ?
                        <Alert style={{marginBottom: '10px'}} type="warning" showIcon message={`Channel sync is paused until ${syncPause}`} />
                        : null
                    }

                    <Form layout="vertical">
                        <Space wrap>
                            <Form.Item label="Property">
                                <Select
                                    style={{minWidth: '220px'}}
                                    value={property}
                                    disabled={!admin}
                                    onChange={onPropertyChange}
                                    showSearch
                                    optionFilterProp="children"
                                    placeholder="Property">
                                    {propertyList.map(value => <Option key={value.id} value={value.id}>{value.name}</Option>)}
                                </Select>
                            </Form.Item>

                            <Form.Item label="Start Date">
                                <DatePicker value={startDate} onChange={onStartChange} allowClear={false} format="DD/MM/YYYY" />
                            </Form.Item>

                            <Form.Item label="End Date">
                                <DatePicker value={endDate} onChange={onEndChange} allowClear={false} format="DD/MM/YYYY" />
                            </Form.Item>

                            <Button style={{marginTop:'7px'}} onClick={onLoad} type="primary" disabled={saving}>Load</Button>

                            <Button style={{marginTop:'7px'}} onClick={onSave} type="primary" danger disabled={pendingCount === 0 || saving} loading={saving}>
                                {`Save${pendingCount > 0 ? ` (${pendingCount})` : ''}`}
                            </Button>
                        </Space>
                    </Form>

                    <div style={{fontSize: '11px', color: '#888', marginBottom: '8px'}}>
                        Type a new daily price in a date cell and press Enter.
                        &nbsp;&nbsp;
                        <span style={{display: 'inline-block', width: '12px', height: '12px', background: '#ffe58f', verticalAlign: 'middle', marginRight: '4px'}}></span>
                        Changed, not saved yet.
                        &nbsp;&nbsp;
                        <span style={{display: 'inline-block', width: '12px', height: '12px', background: '#ffccc7', verticalAlign: 'middle', marginRight: '4px'}}></span>
                        Changed by more than 50%. Nothing is sent until you press Save.
                    </div>

                    <Table dataSource={rows} columns={columns} rowKey="key" loading={loading}
                           scroll={{ x: 330 + (dates.length * 105)}}
                           bordered
                           size="small"
                           pagination={false}
                    >
                    </Table>

                </div>
            </Content>
            </Spin>
        </Layout>
        </>
    );
}

export default RateControl
